import { useEffect, useRef } from "react";
import { Phone, ExternalLink, AlertCircle } from "lucide-react";

const MA_RESOURCES = [
  { name: "Parent/Professional Advocacy League (PPAL)", desc: "Family-driven organization offering caregiver support, training, and systems navigation for children's mental health.", url: "https://www.ppal.net" },
  { name: "Massachusetts Family TIES", desc: "Caregiver-to-caregiver emotional support and guidance for families navigating child-serving systems.", url: "https://www.massfamilyties.org" },
  { name: "Massachusetts Behavioral Health Help Line (BHHL)", desc: "Free, confidential 24/7 access to mental health and substance use services across Massachusetts.", url: "https://www.masshelpline.com", phone: "833-773-2445" },
  { name: "Massachusetts Department of Mental Health (DMH)", desc: "State-funded mental health services, supports, and resources for children, adults, and families.", url: "https://www.mass.gov/orgs/department-of-mental-health" },
  { name: "Department of Children and Families (DCF)", desc: "Community-based family support programs and in-home services for families under stress.", url: "https://www.mass.gov/orgs/department-of-children-and-families" },
  { name: "Massachusetts Association for Mental Health (MAMH)", desc: "Mental health advocacy, education, and policy resources.", url: "https://www.mamh.org" },
  { name: "Federation for Children with Special Needs", desc: "Training, advocacy, and information for caregivers of children with special health and mental health needs.", url: "https://fcsn.org" },
  { name: "Samaritans of Massachusetts", desc: "Emotional support and suicide prevention services.", url: "https://samaritanshope.org", phone: "877-870-4673" },
];

const NATIONAL_RESOURCES = [
  { name: "Crisis Text Line", desc: "Immediate emotional support for caregivers in distress.", url: "https://www.crisistextline.org", phone: "Text HOME to 741741 (24/7)" },
  { name: "988 Suicide & Crisis Lifeline", desc: "Free, confidential emotional support and crisis intervention nationwide.", url: "https://988lifeline.org", phone: "Call or text 988" },
  { name: "Caregiver Action Network (CAN)", desc: "Practical self-care tools, stress management resources, and caregiver education.", url: "https://caregiveraction.org" },
  { name: "Mental Health America (MHA)", desc: "Mental health education, screening tools, and caregiver-focused resources.", url: "https://www.mhanational.org" },
  { name: "Psychology Today – Therapist Finder", desc: "Search for therapists and caregiver support groups by location, insurance, and specialty.", url: "https://www.psychologytoday.com" },
  { name: "Child Mind Institute", desc: "Evidence-based resources for families supporting children with mental health and learning challenges.", url: "https://childmind.org" },
  { name: "SAMHSA National Helpline", desc: "Treatment referral and information service for mental health and substance use.", url: "https://www.samhsa.gov/find-help/national-helpline", phone: "1-800-662-HELP (4357)" },
  { name: "Family Voices", desc: "National network providing family-to-family support and advocacy for children with special health care needs.", url: "https://familyvoices.org" },
  { name: "The Mighty", desc: "Online peer community sharing lived experiences around mental health, caregiving, and resilience.", url: "https://themighty.com" },
];

function useScrollReveal(ref) {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((entry) => { if (entry.isIntersecting) entry.target.querySelectorAll(".animate-on-scroll, .slide-left, .slide-right, .scale-in, .blur-in, .stagger-item, .line-animate").forEach((el) => el.classList.add("visible")); }),
      { threshold: 0.05 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref]);
}

function ResourceCard({ resource, index }) {
  return (
    <div data-testid={`resource-${index}`} className={`animate-on-scroll delay-${Math.min((index % 4 + 1) * 100, 400)} p-6 rounded-2xl bg-white border border-[#C8DDD8]/50 hover:border-[#0E8DB5]/30 transition-all duration-300 hover:shadow-lg hover:-translate-y-1`}>
      <h4 className="text-lg font-serif font-bold text-earth mb-2">{resource.name}</h4>
      <p className="text-sm font-sans text-earth-muted leading-relaxed mb-3">{resource.desc}</p>
      <div className="flex flex-wrap gap-3">
        <a href={resource.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs font-sans font-semibold text-[#0E8DB5] hover:text-[#0A7599] no-underline transition-colors">
          <ExternalLink className="w-3.5 h-3.5" /> Visit Website
        </a>
        {resource.phone && (
          <span className="inline-flex items-center gap-1.5 text-xs font-sans font-semibold text-[#3DAA35]">
            <Phone className="w-3.5 h-3.5" /> {resource.phone}
          </span>
        )}
      </div>
    </div>
  );
}

export default function CaregiverResources() {
  const sectionRef = useRef(null);
  useScrollReveal(sectionRef);

  return (
    <main ref={sectionRef} className="pt-24">
      {/* Header */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-[#1A3A4A] to-[#1A3A4A]/90 text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-[#0E8DB5]/15 rounded-full translate-x-1/3 -translate-y-1/3 blur-3xl" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="animate-on-scroll max-w-3xl">
            <p className="text-xs font-sans tracking-[0.25em] uppercase font-semibold text-[#7CC8E0] mb-4">
              <span className="inline-block w-10 h-[2px] bg-[#0E8DB5] mr-3 align-middle" />Support & Resources
            </p>
            <h1 data-testid="caregiver-heading" className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold tracking-tight leading-tight mb-6">
              Caregiver <span className="text-[#7CC8E0]">Resources</span>
            </h1>
            <p className="text-base sm:text-lg font-sans font-medium text-white/80">
              When you're caring for a child in crisis, you need answers — not another waiting list. Here are local and national mental health resources to support you.
            </p>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-4 bg-[#E8F5E1]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-[#3DAA35] flex-shrink-0" />
          <p className="text-sm font-sans text-earth-muted">These resources are provided for informational purposes only and are not a substitute for professional medical or mental health care.</p>
        </div>
      </section>

      {/* Massachusetts Resources */}
      <section className="py-20 bg-[#F8FAFB]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="animate-on-scroll text-3xl sm:text-4xl font-serif font-bold tracking-tight text-earth mb-3">
            Massachusetts <span className="gradient-text">Caregiver Support</span>
          </h2>
          <p className="animate-on-scroll delay-100 text-base font-sans text-earth-muted mb-10">Local resources for families in Massachusetts.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {MA_RESOURCES.map((r, i) => <ResourceCard key={r.name} resource={r} index={i} />)}
          </div>
        </div>
      </section>

      {/* National Resources */}
      <section className="py-20 bg-bone">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="animate-on-scroll text-3xl sm:text-4xl font-serif font-bold tracking-tight text-earth mb-3">
            National <span className="gradient-text">Caregiver Support</span>
          </h2>
          <p className="animate-on-scroll delay-100 text-base font-sans text-earth-muted mb-10">Resources available across the United States.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {NATIONAL_RESOURCES.map((r, i) => <ResourceCard key={r.name} resource={r} index={i} />)}
          </div>
        </div>
      </section>
    </main>
  );
}
